import { useEffect, useState, useRef } from 'react'
import { useSelector } from 'react-redux'

import { getIdentity, isGhost, isStudent, isTeacher } from 'helpers/twilio'
import {
    _latestConnectedParticipant,
    _latestDisconnectedParticipant,
} from 'store/selectors/Room'

import useVideoContext from '../useVideoContext/useVideoContext'

export { getIdentity } // FIXME: fallback export for current getIdentity import from multiple files, refactor it later

export default function useParticipants() {
    const isMountedRef = useRef(false)
    const { room } = useVideoContext()
    const data = room ? Array.from(room.participants.values()) : []
    const [participants, setParticipants] = useState(data)
    const [newParticipant, setNewParticipant] = useState(null) // new Participant joined
    const [participantLeft, setParticipantLeft] = useState(null)
    const [teacher, setTeacher] = useState(null)
    const [ghost, setGhost] = useState(null)
    const [remoteStudents, setRemoteStudents] = useState(
        data.filter(d => isStudent(d.identity))
    )

    const connectedParticipant = useSelector(_latestConnectedParticipant)
    const disconnectedParticipant = useSelector(_latestDisconnectedParticipant)

    const addStudent = student => {
        if (isMountedRef.current) {
            if (!student) return

            setRemoteStudents(prevStudents => {
                const index = prevStudents.findIndex(
                    prevStudent => prevStudent.identity === student.identity
                )
                if (index >= 0) return prevStudents
                return [...prevStudents, student]
            })
        }
    }

    const removeStudent = student => {
        if (isMountedRef.current) {
            if (!student) return

            setRemoteStudents(prevStudents => {
                const index = prevStudents.findIndex(
                    prevStudent => prevStudent.identity === student.identity
                )
                if (index < 0) return prevStudents
                return [
                    ...prevStudents.slice(0, index),
                    ...prevStudents.slice(index + 1),
                ]
            })
        }
    }

    const participantConnected = participant => {
        if (isMountedRef.current) {
            setParticipants(prevParticipants => [
                ...prevParticipants,
                participant,
            ])

            !isGhost(participant.identity) && setNewParticipant(participant)
            isGhost(participant.identity) && setGhost(participant.identity)

            if (isStudent(participant.identity)) {
                addStudent(participant)
            }
        }
    }
    const participantDisconnected = participant => {
        if (isMountedRef.current) {
            setParticipants(prevParticipants =>
                prevParticipants.filter(p => p !== participant)
            )

            !isGhost(participant.identity) && setParticipantLeft(participant)
            isStudent(participant.identity) && removeStudent(participant)
            isGhost(participant.identity) && setGhost(null)
        }
    }

    useEffect(() => {
        participantConnected(connectedParticipant)
    }, [connectedParticipant])

    useEffect(() => {
        participantDisconnected(disconnectedParticipant)
    }, [disconnectedParticipant])

    useEffect(() => {
        isMountedRef.current = true
        return () => {
            isMountedRef.current = false
        }
    }, [])

    useEffect(() => {
        const isLocalTeacher = isTeacher(room?.localParticipant?.identity)
        let _teacher = null

        if (isLocalTeacher) {
            _teacher = room.localParticipant
        } else {
            _teacher = participants.find(p => isTeacher(p.identity))
        }

        setTeacher(_teacher)
    }, [participants, room])

    const isRemoteUserAvailableInRoom = userId => {
        return (
            participants.findIndex(participant =>
                participant.identity.includes(userId)
            ) > -1
        )
    }

    return {
        teacher,
        participants,
        newParticipant,
        setNewParticipant,
        remoteStudents,
        participantLeft,
        ghost,
        isRemoteUserAvailableInRoom,
        localParticipant: room?.localParticipant,
        allParticipants: [room?.localParticipant, ...participants],
    }
}
